import type { ExtractedData } from '@/types/quote';
import { useTranslation } from 'react-i18next';

interface ExtractedDataPanelProps {
    data: ExtractedData;
}

export const ExtractedDataPanel = ({ data }: ExtractedDataPanelProps) => {
    const { t } = useTranslation('pages:quotes');

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary mb-4">{t('detail.extractedData.title')}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.product && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.product')}</div>
                        <div className="text-sm text-primary">{data.product}</div>
                    </div>
                )}

                {data.origin && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.origin')}</div>
                        <div className="text-sm text-primary">{data.origin}</div>
                    </div>
                )}

                {data.destination && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.destination')}</div>
                        <div className="text-sm text-primary">{data.destination}</div>
                    </div>
                )}

                {data.quantity !== undefined && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.quantity')}</div>
                        <div className="text-sm text-primary">
                            {data.quantity} {data.unit || ''}
                        </div>
                    </div>
                )}

                {data.pickupDate && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.pickupDate')}</div>
                        <div className="text-sm text-primary">
                            {data.pickupDate.toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </div>
                    </div>
                )}

                {data.deliveryDate && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.deliveryDate')}</div>
                        <div className="text-sm text-primary">
                            {data.deliveryDate.toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </div>
                    </div>
                )}

                {data.estimatedValue !== undefined && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.estimatedValue')}</div>
                        <div className="text-sm text-primary">
                            {data.estimatedValue.toLocaleString('it-IT')} {data.currency || 'EUR'}
                        </div>
                    </div>
                )}

                {data.equipmentType && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.equipmentType')}</div>
                        <div className="text-sm text-primary">{data.equipmentType}</div>
                    </div>
                )}

                {data.temperatureRequirements && (
                    <div className="rounded-lg bg-secondary p-4">
                        <div className="text-xs font-medium text-tertiary mb-1">{t('extracted.temperatureRequirements')}</div>
                        <div className="text-sm text-primary">{data.temperatureRequirements}</div>
                    </div>
                )}
            </div>

            {data.specialRequirements && data.specialRequirements.length > 0 && (
                <div className="mt-4">
                    <div className="text-xs font-medium text-tertiary mb-2">{t('extracted.specialRequirements')}</div>
                    <div className="flex flex-wrap gap-2">
                        {data.specialRequirements.map((req, idx) => (
                            <span
                                key={idx}
                                className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-primary"
                            >
                                {req}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {data.customsRequirements && (
                <div className="mt-4">
                    <div className="text-xs font-medium text-tertiary mb-2">{t('extracted.customsRequirements')}</div>
                    <div className="flex flex-wrap gap-2">
                        {Array.isArray(data.customsRequirements) ? (
                            data.customsRequirements.map((req: string, idx: number) => (
                                <span
                                    key={idx}
                                    className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-primary"
                                >
                                    {req}
                                </span>
                            ))
                        ) : (
                            <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-primary">
                                {data.customsRequirements}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

